import {ApolloError} from "@apollo/client";
import {t} from "../i18n";
import {Toast} from "antd-mobile";
import {CloseCircleFill, CloseOutline, ExclamationCircleFill} from "antd-mobile-icons";
import {playRepeatedScanSound} from "./scanUtils";

export interface ParseExceptionResult {
    title: string;
    message: string;
}

// 类型守卫函数，用于检查对象是否符合ParseExceptionResult接口
const isParseExceptionResult = (obj: any): obj is ParseExceptionResult => {
    return obj && typeof obj === 'object' && 
           typeof obj.title === 'string' && 
           typeof obj.message === 'string';
};

// 处理状态翻译的辅助函数
const translateStatusInMessage = (message: string): string => {
    // 定义所有可能的状态值
    const statusValues = [
        'CREATED', 'CANCELLED', 'ARRIVED_ORIGIN_HUB', 'PENDING_SHIPMENT', 'HANDED_OVER',
        'IN_TRANSIT', 'ARRIVED_DESTINATION_HUB', 'ARRIVED_WHS', 
        'WAITING_FOR_DELIVERY', 'DELIVERY_IN_PROGRESS', 'DELIVERED', 
        'DELIVERY_FAILED', 'PENDING_INTERCEPTION', 'INTERCEPTED', 
        'RETURN_STARTED', 'RETURNED', 'HOLDING', 'RETURN_ARRIVED_DESTINATION_HUB', 
        'PICKUP_FAILED'
    ];
    
    // 检查消息中是否包含任何状态值
    for (const statusValue of statusValues) {
        if (message.includes(statusValue)) {
            const statusKey = `common.overallStatus.${statusValue}`;
            const translatedStatus = t(statusKey);
            
            // 如果找到了翻译，进行替换
            if (translatedStatus !== statusKey) {
                // 处理英文错误消息，替换为中文模板
                return message.replace(statusValue, translatedStatus);
            }
        }
    }
    return message;
};

export const parseException = (error?: any): ParseExceptionResult => {


    if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        const details = graphQLError.extensions?.details;
        console.log("messageKey:"+graphQLError.extensions?.messageKey)
        const titleKey = `${graphQLError.extensions?.messageKey}.title`;
        let title = t(titleKey as string, details);
        if (title === titleKey) {
            // 如果没有找到.title键，尝试从原始键的对象结构中提取title
            const originalKey = `${graphQLError.extensions?.messageKey}`;
            const translationResult = t(originalKey, details);
            if (typeof translationResult === 'object' && translationResult !== null && 'title' in translationResult) {
                title = (translationResult as { title: string }).title;
            } else {
                title = t('errors.title');
            }
        }

        let messageKey = `${graphQLError.extensions?.messageKey}.message`;
        let message = t(messageKey, details);
        if (message === messageKey) {
            messageKey = `${graphQLError.extensions?.messageKey}`;
            const translationResult = t(messageKey, details);
            // 如果翻译结果是对象，提取message属性；否则直接使用
            if (typeof translationResult === 'object' && translationResult !== null && 'message' in translationResult) {
                message = (translationResult as { message: string }).message;
            } else {
                message = translationResult;
            }
        }

        if(graphQLError.extensions?.messageKey == "errors.business.pda.returnScanNotAllowed"
            || graphQLError.extensions?.messageKey == "errors.business.pda.returnToWhsScanNotAllowed") {
            // 处理状态翻译
            message = translateStatusInMessage(message);
        }
        
        return { 
            title: title,
            message: message
        }
    }
    return {
        title: t('errors.title'),
        message: t('errors.system.internal')
    }
};

export const parseExceptionMessageKey = (error?: any): string => {
    if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        let messageKey = `${graphQLError.extensions?.messageKey}`;
        return messageKey
    }
    return  'errors.system.internal'
};

export const showError = (error?: any, duration?: number) => {
    const {title, message} = isParseExceptionResult(error) ? error : parseException(error);
    const showCloseButton = duration === 0;
    
    Toast.show({
        content: (
            <div className="relative w-full">
                <div>
                    <div className="flex items-start justify-between mb-1">
                        <div className="w-6" />
                        <div className="flex items-center gap-2 justify-center flex-1">
                        <CloseCircleFill fontSize={20} color='var(--adm-color-danger)'/>
                        <div className="text-base">{title}</div>
                    </div>
                        <div className="w-6 flex justify-end">
                            {showCloseButton && (
                                <button
                                    onClick={() => Toast.clear()}
                                    className="p-0.5 hover:opacity-70 transition-opacity"
                                    aria-label="关闭"
                                >
                                    <CloseOutline fontSize={18} />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="text-sm text-center">{message}</div>
                </div>
            </div>
        ),
        // 不使用 Toast 的 icon 区域：避免 icon+content 的布局导致关闭按钮无法贴到 Toast 右上角
        duration: duration !== undefined ? duration : 2000
    });
};

export const showErrorNoTitle = (error?: any) => {
    const { message } = parseException(error);
    let showMessage : string;
    switch ( message){
        case t('errors.business.alreadyScanned'):
            showMessage = message;
            // 播放重复扫描声音
            playRepeatedScanSound();
            break;
        case t('errors.business.alreadyFinished'):
            showMessage = message;
            break;
        case t('errors.business.order.alreadyFinished'):
            showMessage = message;
            break;
        case t('errors.business.alreadyEnterDeliveryPage'):
            showMessage = message;
            break;
        case t('errors.business.alreadyEnterOthersDeliveryPage'):
            showMessage = message;
            break;
        case t('errors.business.transferInPage'):
            showMessage = message;
            break;
        case t('errors.business.alreadyEnterFailDeliveryPage'):
            showMessage = message;
            break;
        default:
            showMessage = t('pages.delivery.exception.showMessage');
    }
    Toast.show({
        content: `${showMessage}`,
        icon: <div className="flex items-center gap-2 justify-center">
            <CloseCircleFill fontSize={20} color='var(--adm-color-danger)'/>
        </div>,
        duration: 2000
    });
};

/**
 * 专门处理重复扫描错误的函数
 * 播放重复扫描声音并显示注意信息
 */
export const showRepeatedScanError = (error?: any, duration?: number) => {
    const { message } = parseException(error);
    const title = t('pda.common.attention');
    // 播放重复扫描声音
    playRepeatedScanSound();
    const showCloseButton = duration === 0;
    
    Toast.show({
        content: (
            <div className="relative w-full">
                <div>
                    <div className="flex items-start justify-between mb-1">
                        <div className="w-6" />
                        <div className="flex items-center gap-2 justify-center flex-1">
                            <ExclamationCircleFill fontSize={20} color='var(--adm-color-warning)'/>
                            <div className="text-base">{title}</div>
                        </div>
                        <div className="w-6 flex justify-end">
                            {showCloseButton && (
                                <button
                                    onClick={() => Toast.clear()}
                                    className="p-0.5 hover:opacity-70 transition-opacity"
                                    aria-label="关闭"
                                >
                                    <CloseOutline fontSize={18} />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="text-sm text-center">{message}</div>
                </div>
            </div>
        ),
        // 不使用 Toast 的 icon 区域：避免 icon+content 的布局导致关闭按钮无法贴到 Toast 右上角
        duration: duration !== undefined ? duration : 2000
    });
};

/**
 * 灵活处理错误并显示 Toast
 */
/**
 * 安全地获取翻译消息，处理对象结构的翻译键
 * @param messageKey 消息键
 * @param fallback 回退消息
 * @returns 翻译后的字符串消息
 */
export const getTranslatedMessage = (messageKey: string, fallback?: string): string => {
    // 先尝试获取 messageKey.message
    const messageWithSuffix = `${messageKey}.message`;
    const messageResult = t(messageWithSuffix);
    
    // 如果找到了 .message 后缀的翻译，使用它
    if (messageResult !== messageWithSuffix) {
        return messageResult;
    }
    
    // 否则尝试原始 messageKey
    const originalResult = t(messageKey);
    
    // 如果原始结果是字符串，返回它
    if (typeof originalResult === 'string') {
        return originalResult;
    }
    
    // 如果原始结果是对象，尝试提取 message 属性
    if (originalResult && typeof originalResult === 'object' && 'message' in originalResult) {
        return (originalResult as any).message;
    }
    
    // 最后使用回退消息
    return fallback || messageKey;
};

export const showErrorToast = (error?: any) => {
    const messageKey = parseExceptionMessageKey(error);

    if (!messageKey || messageKey === 'errors.system.internal') {
        Toast.show({
            content: t('errors.system.internal'),
        });
        return;
    }

    // 使用安全的翻译函数
    const content = getTranslatedMessage(messageKey);

    Toast.show({
        content: content,
    });
};
